import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { DomainPageComponent } from '@technocom/core';
import { PAGER_OPTIONS, PAGER_SIZE } from '@app/shared/constants/grid-options';
import { SortDescriptor } from '@progress/kendo-data-query';
import { SecurityService } from '@technocom/auth';
import { Role } from '@app/shared/models/user/role';
import { DialogService } from '@technocom/ui/dialogs';
import { filter, finalize, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';
import { ActivatedRoute, ChildActivationEnd, Router } from '@angular/router';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { EntityProvider, RemoteEntityDataSource, SimpleAttachmentLinkService } from '@technocom/data';
import { DEFAULT_PAGE_SIZE, Entity, EntityTypeOf } from '@technocom/data/core';
import { TranslocoService } from '@ngneat/transloco';
import { Localizable } from '@app/shared/models/localizable/localizable';
import { QueryParamsLike } from '@technocom/common/data';

function deepFieldExtractor(entity: Entity, field: string) {
  if (field.includes('.')) {
    const dotIndex = field.indexOf('.');
    return deepFieldExtractor(entity.get(field.slice(0, dotIndex)), field.slice(dotIndex + 1));
  }
  return entity.get(field);
}

@Component({
  selector: 'app-cards-list',
  templateUrl: './cards-list.component.html',
  styleUrls: ['./cards-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardsListComponent<T extends Localizable = Localizable>
  extends DomainPageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  pagerConfig = { ...PAGER_OPTIONS, total: 0, skip: 0, size: PAGER_SIZE };
  defaultDateSort: SortDescriptor[] = [
    { field: 'postDate', dir: 'desc' },
    { field: 'id', dir: 'asc' },
  ];

  loading = true;
  fetchLang: string;

  observer: ResizeObserver;
  columnsCount = 0;
  searchText = '';
  lastSearchText = '';
  isSearched = false;

  dataSource: RemoteEntityDataSource;
  data: Array<T> = [];

  @ViewChild('gridList') gridList;
  @ViewChild('searchBox', { static: true }) searchBoxCmp: TextBoxComponent;

  @Input()
  model: EntityTypeOf<Entity>;

  @Input()
  @Optional()
  sourceUrl: string;

  @Input()
  withPreview = true;

  @Input()
  primary = false;

  @Input()
  @Optional()
  groupSeparator: { field: string; values: any[] };

  @Input()
  @Optional()
  customActions: {
    open?: (dataItem) => void;
    edit?: (dataItem) => void;
    add?: () => void;
  };

  constructor(
    // Заменяем на грибов-ui
    private entityProvider: EntityProvider,
    private securityService: SecurityService,
    private attachService: SimpleAttachmentLinkService,
    //
    private cd: ChangeDetectorRef,
    private dialogService: DialogService,
    private router: Router,
    route: ActivatedRoute,
    private ngZone: NgZone,
    private localize: TranslocoService,
  ) {
    super(route);
    this.observer = new ResizeObserver(entries => {
      this.updColumnsCount();
      this.cd.detectChanges();
    });
  }

  get hasGroups(): boolean {
    return !_.isNil(this.groupSeparator?.values);
  }

  // Проверка контура, нужна для выбора способа получения локализации и доступа к инструментам редактирования
  get isPublic(): boolean {
    return !this.securityService.canUserAccess([Role.OPR, Role.DATA_ADM]);
  }

  getGroupItems(value: any) {
    return this.data?.filter(i => deepFieldExtractor(i, this.groupSeparator.field) === value);
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (!this.model) {
      this.model = this.routeSnapshot.data.model;
    }
    this.attach(
      this.router.events
        .pipe(filter(event => event instanceof ChildActivationEnd))
        .subscribe((event: ChildActivationEnd) => {
          this.resolveDataSource();
        }),
    );
    this.attach(
      this.localize.langChanges$.pipe(switchMap(lang => this.route.data)).subscribe(lang => {
        this.resolveDataSource();
        if (this.dataSource) this.fetchData(this.dataSource.state);
        this.cd.detectChanges();
      }),
    );
  }

  resolveDataSource() {
    this.isSearched = this.routeSnapshot.data.isSearched;
    if (this.isSearched) {
      this.searchText = this.routeSnapshot.data.dataObject.headline.slice(7);
      this.lastSearchText = this.searchText + '';
      this.data = this.routeSnapshot.data.dataObject.records;
      this.loading = false;
    } else {
      this.dataSource = this.entityProvider
        .getRepository(this.model)
        .getDataSource(this.sourceUrl ? { url: this.sourceUrl, params: { unPage: true } } : void 0);
      this.dataSource.setState({ take: DEFAULT_PAGE_SIZE, sort: [{ field: 'postDate', dir: 'desc' }] });
      this.dataSource.sort(this.defaultDateSort);
      this.fetchData();
    }
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    // Блок контроля сжатия\расширения Зоны карточек
    this.observer.observe(this.gridList.nativeElement);
    if (this.route.snapshot.data.isSearched) {
      // если мы находимся на странице поиска, то при клике на X редирект на новости
      const textBoxClearValueFn = this.searchBoxCmp.clearValue;
      this.searchBoxCmp.clearValue = (...args) => {
        this.ngZone.run(() => this.router.navigate([this.model.getClientRoot()]));
        return textBoxClearValueFn.apply(this.searchBoxCmp, args);
      };
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.observer.unobserve(this.gridList.nativeElement);
  }

  groups() {
    return this.groupSeparator.values;
  }

  private fetchData(state?: QueryParamsLike) {
    this.loading = true;
    this.fetchLang = this.localize.getActiveLang();
    if (state) this.dataSource.setState(state);
    this.attach(
      this.dataSource
        .query(this.dataSource.state)
        .pipe(
          finalize(() => {
            this.loading = false;
            this.cd.detectChanges();
          }),
        )
        .subscribe((result: any) => {
          this.data = result.data;
          this.cd.detectChanges();
        }),
    );
  }

  search() {
    if (!_.isEmpty(this.searchText)) {
      this.router.navigate([this.model.getClientRoot(), 'search', this.searchText], { onSameUrlNavigation: 'reload' });
    } else this.router.navigate([this.model.getClientRoot()], { onSameUrlNavigation: 'reload' });
  }

  add() {
    if (this.customActions?.add) {
      this.customActions.add();
      return;
    }
    this.router.navigate([this.model.getClientRoot(), 'add', 'edit']);
  }

  onDelete(item: any) {
    this.fetchData();
  }

  updColumnsCount() {
    const width = this.gridList?.nativeElement.scrollWidth;
    this.columnsCount = width / 430;
  }

  onPageChange(e: { skip: number; take: number }) {
    this.fetchData({ ...e, skip: e.skip });
  }
}
